import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { BlockContext, BlockRule, BlockedResult } from "../types";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

type MetaDmChannel = "facebookDm" | "instagramDm";

const META_DM_CHANNEL_LABEL: Record<MetaDmChannel, string> = {
  facebookDm: "Facebook",
  instagramDm: "Instagram",
};

function isMetaDmChannel(channel: string): channel is MetaDmChannel {
  return channel === "facebookDm" || channel === "instagramDm";
}

function findLastCustomerDm(
  messages: readonly Message[],
  channel: MetaDmChannel,
): Message | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (!msg) continue;
    if (msg.channel === channel && msg.senderType === "customer") {
      return msg;
    }
  }
  return undefined;
}

/**
 * Meta's messaging API only allows sending a DM within 7 days of the most
 * recent customer DM. Mirrors the v1 `canSendMetaMessage` business rule in
 * `redo/merchant/support/app/src/utils.ts`.
 */
export const metaSevenDayRule: BlockRule = (
  ctx: BlockContext,
): BlockedResult | null => {
  if (!isMetaDmChannel(ctx.channel)) return null;
  // Empty `messages` during load is indistinguishable from "no customer DM
  // ever" — don't block while we're still waiting for data.
  if (ctx.messagesLoading) return null;

  const lastCustomerDm = findLastCustomerDm(ctx.messages, ctx.channel);
  const withinWindow =
    lastCustomerDm !== undefined &&
    Date.now() - new Date(lastCustomerDm.sentAt).getTime() < SEVEN_DAYS_MS;

  if (withinWindow) return null;

  const label = META_DM_CHANNEL_LABEL[ctx.channel];
  return {
    reason: "meta-seven-day-window",
    message: `Can't send messages through ${label}'s API more than 7 days after the most recent customer message.`,
  };
};
